import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import {
	useConnectFurnitureToEref,
	useDeleteFurnitureComment,
	useDisconnectFurnitureFromEref,
	useUpdateFurniture,
	useUpdateFurnitureCategory,
	useUpdateFurnitureComment,
	useUpdateFurnitureConfig,
	useUpdateFurnitureFixedPrice,
	useUpdateFurnitureStatus,
} from "hooks/api/quotation/quotations.hook"
import { useGetModelNumber, useGetModelNumberImageLink } from "hooks/api/modelNumbers/modelNumbers.hook"
import { useObjectsCategories } from "hooks/api/objectsCategories/objectsCategories.hook"
import { useGetSeries } from "hooks/api/series/series.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { useEntityMutationQueue } from "hooks/mutationQueue.hook"
import { reorderFurnitureInCache, spliceFurnitureIntoCache } from "helpers/quotationCache.helper"
import AnnotationService from "services/annotations.service"
import { FurnitureHeader } from "./layout/FurnitureHeader/FurnitureHeader"
import { FurnitureImage } from "./layout/FurnitureImage/FurnitureImage"
import { ElevationReferenceSelect } from "./layout/ElevationReferenceSelect/ElevationReferenceSelect"
import { CategoriesSection } from "./layout/CategoriesSection/CategoriesSection"
import { DimensionsSection } from "./layout/DimensionsSection/DimensionsSection"
import { ModelNumberSection } from "./layout/ModelNumberSection/ModelNumberSection"
import { PriceSection } from "./layout/PriceSection/PriceSection"
import { NameSection } from "./layout/NameSection/NameSection"
import { AttachmentsSection } from "./layout/AttachmentsSection/AttachmentsSection"
import { NotesSection } from "./layout/NotesSection/NotesSection"
import { FurnitureFooter } from "./layout/FurnitureFooter/FurnitureFooter"
import { ConnectedCalloutsTab } from "./layout/ConnectedCalloutsTab/ConnectedCalloutsTab"
import { SegmentedWithoutBg } from "components/ui/segmented/SegmentedWithoutBg/SegmentedWithoutBg"
import { asOtherFurniture, isOtherFurniture } from "components/ui/customWebviewer/FurnitureModelLabel/furnitureModel.helpers"
import { OTHER_TOP_LEVEL_CATEGORY } from "./furniture.constants"
import { furnitureQueueKey } from "./furnitureQueueKey"
import { useFurnitureForm } from "./useFurnitureForm.hook"
import type {
	TEcall,
	TEref,
	TFurniture,
	TFurnitureFixedPriceConfig,
	TFurnitureStatus,
	TFurnitureEstimationConfig,
	TPageElevationData,
	TSelectedModelNumber,
	TUpdateFurnitureConfigRequest,
	TUpdateFurnitureResponse,
} from "types/elevationReferences.type"
import type { TModelNumberDetails } from "types/modelNumbers.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { AxiosResponse } from "axios"
import style from "./FurnitureSidebar.module.scss"

const { sidebar, body, divider, content, segmentedWrapper, segmented } = style

type TabOption = "Details" | "Connected callouts"
const TAB_OPTIONS: TabOption[] = ["Details", "Connected callouts"]

const EMPTY_RELATED_OBJECTS: TPageElevationData[] = []

const bumpErefVersionInCache = (
	cached: AxiosResponse<TQuotationDetail> | undefined,
	erefId: number,
	newVersion: number,
): AxiosResponse<TQuotationDetail> | undefined => {
	if (!cached?.data?.related_objects) return cached
	let changed = false
	const nextPages = cached.data.related_objects.map((page) => {
		const idx = page.erefs.findIndex((e) => e.id === erefId)
		if (idx === -1) return page
		changed = true
		const nextErefs = page.erefs.slice()
		nextErefs[idx] = { ...nextErefs[idx], version: newVersion }
		return { ...page, erefs: nextErefs }
	})
	if (!changed) return cached
	return { ...cached, data: { ...cached.data, related_objects: nextPages } }
}

type Props = {
	furniture: TFurniture
	relatedObjects?: TPageElevationData[]
	connectedEcalls?: TEcall[]
	initialTab?: TabOption
	onView?: (ecall: TEcall) => void
	onClose?: () => void
	onNavigateToEref?: (furniture: TFurniture) => void
}

export const FurnitureSidebar = ({
	furniture,
	relatedObjects = EMPTY_RELATED_OBJECTS,
	connectedEcalls = [],
	initialTab = "Details",
	onView,
	onClose,
	onNavigateToEref,
}: Props) => {
	const form = useFurnitureForm(furniture)
	const [tab, setTab] = useState<TabOption>(initialTab)

	const [searchParams] = useSearchParams()
	const quotationId = Number(searchParams.get("quotationID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { mutateAsync: updateFurnitureCategoryAsync } = useUpdateFurnitureCategory()
	const { mutateAsync: updateFurnitureFixedPriceAsync } = useUpdateFurnitureFixedPrice()
	const { mutateAsync: updateFurnitureAsync } = useUpdateFurniture()
	const { mutateAsync: updateFurnitureConfigAsync } = useUpdateFurnitureConfig()
	const { mutateAsync: updateFurnitureCommentAsync } = useUpdateFurnitureComment()
	const { mutateAsync: deleteFurnitureCommentAsync } = useDeleteFurnitureComment()
	const { mutateAsync: updateFurnitureStatusAsync, isPending: isStatusPending } = useUpdateFurnitureStatus()
	const { mutateAsync: connectFurnitureToEref } = useConnectFurnitureToEref()
	const { mutateAsync: disconnectFurnitureFromEref } = useDisconnectFurnitureFromEref()
	const { enqueue, versionRef } = useEntityMutationQueue(furnitureQueueKey(furniture.id), furniture.version)
	const { data: objectsCategoriesData } = useObjectsCategories()
	const savedWidthRef = useRef(furniture.width)
	const savedHeightRef = useRef(furniture.height)
	const savedDepthRef = useRef(furniture.depth)
	const savedAdjustedShelvesRef = useRef<number | null>(furniture.estimation_config?.adjusted_shelves ?? null)
	useEffect(() => {
		savedAdjustedShelvesRef.current = furniture.estimation_config?.adjusted_shelves ?? null
	}, [furniture.estimation_config?.adjusted_shelves])
	const savedNotesRef = useRef(furniture.estimation_config_comment ?? "")
	const savedPriceRef = useRef<number | null>(furniture.fixed_price_config?.fixed_price ?? null)
	useEffect(() => {
		savedPriceRef.current = furniture.fixed_price_config?.fixed_price ?? null
	}, [furniture.fixed_price_config?.fixed_price])
	const savedNameRef = useRef<string>(furniture.fixed_price_config?.name ?? "")
	useEffect(() => {
		savedNameRef.current = furniture.fixed_price_config?.name ?? ""
	}, [furniture.fixed_price_config?.name])

	const connectedEref = useMemo<TEref | null>(() => {
		if (!furniture.eref_id) return null
		for (const page of relatedObjects) {
			const found = page.erefs.find((e) => e.id === furniture.eref_id)
			if (found) return found
		}
		return null
	}, [relatedObjects, furniture.eref_id])
	const seriesOverrideId = connectedEref?.estimation_config?.series_override_id ?? null

	const commitDimension = useCallback(
		(key: "width" | "height" | "depth", value: number | null) => {
			if (!Number.isFinite(quotationId) || !quotationId) return
			const savedRef = key === "width" ? savedWidthRef : key === "height" ? savedHeightRef : savedDepthRef
			if (value === savedRef.current) return

			enqueue(async () => {
				try {
					const response = await updateFurnitureAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, [key]: value },
					})
					const data = response?.data as TUpdateFurnitureResponse | undefined
					const updated = data?.this_furniture
					if (!updated) return
					versionRef.current = updated.version
					savedWidthRef.current = updated.width
					savedHeightRef.current = updated.height
					savedDepthRef.current = updated.depth
					const ordering = data?.ordering
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
						const next = spliceFurnitureIntoCache(cached, updated)
						return ordering ? reorderFurnitureInCache(next, ordering) : next
					})
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[quotationId, enqueue, versionRef, updateFurnitureAsync, furniture.id, queryClient, formatError],
	)

	const commitSelectedModelNumber = useCallback(
		(next: TSelectedModelNumber | null): Promise<void> => {
			if (!Number.isFinite(quotationId) || !quotationId) return Promise.resolve()

			return enqueue(async () => {
				try {
					const response = await updateFurnitureAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, selected_model_number_id: next?.id ?? null },
					})
					const data = response?.data as TUpdateFurnitureResponse | undefined
					const updated = data?.this_furniture
					if (!updated) {
						form.setSelectedModelNumber(next)
						return
					}
					versionRef.current = updated.version
					const ordering = data?.ordering
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
						const nextCache = spliceFurnitureIntoCache(cached, updated)
						return ordering ? reorderFurnitureInCache(nextCache, ordering) : nextCache
					})
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
					throw e
				}
			})
		},
		[form, quotationId, enqueue, versionRef, updateFurnitureAsync, furniture.id, queryClient, formatError],
	)

	const commitConfig = useCallback(
		(body: Omit<TUpdateFurnitureConfigRequest, "version">) => {
			if (!Number.isFinite(quotationId) || !quotationId) return

			enqueue(async () => {
				try {
					const response = await updateFurnitureConfigAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, ...body },
					})
					const { version: responseVersion, ...configFields } =
						(response?.data as (Partial<TFurnitureEstimationConfig> & { version?: number }) | undefined) ?? {}
					const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
					versionRef.current = nextVersion

					const baseConfig: TFurnitureEstimationConfig = furniture.estimation_config ?? {
						furniture_id: furniture.id,
						number_of_fillers: 0,
						number_of_finishing_ends: 0,
						lock_count: null,
						has_locks: false,
						adjusted_shelves: null,
					}

					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
						spliceFurnitureIntoCache(cached, {
							...furniture,
							version: nextVersion,
							estimation_config: { ...baseConfig, ...configFields },
						}),
					)
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[quotationId, enqueue, versionRef, updateFurnitureConfigAsync, furniture, queryClient, formatError],
	)

	const commitNotes = useCallback(() => {
		const next = form.notes
		if (next === savedNotesRef.current) return
		if (!Number.isFinite(quotationId) || !quotationId) return
		savedNotesRef.current = next

		const applyCommentInCache = (commentValue: string | null, nextVersion: number) => {
			queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
				spliceFurnitureIntoCache(cached, {
					...furniture,
					version: nextVersion,
					estimation_config_comment: commentValue,
				}),
			)
		}

		enqueue(async () => {
			try {
				const response =
					next.trim() === ""
						? await deleteFurnitureCommentAsync({
								quotationId,
								furnitureId: furniture.id,
								version: versionRef.current,
							})
						: await updateFurnitureCommentAsync({
								quotationId,
								furnitureId: furniture.id,
								body: { version: versionRef.current, comment: next },
							})
				const responseVersion = (response?.data as { version?: number } | undefined)?.version
				const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
				versionRef.current = nextVersion
				applyCommentInCache(next.trim() === "" ? null : next, nextVersion)
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}, [
		form.notes,
		quotationId,
		enqueue,
		versionRef,
		updateFurnitureCommentAsync,
		deleteFurnitureCommentAsync,
		furniture,
		queryClient,
		formatError,
	])

	const handleErefChange = useCallback(
		async (nextErefId: number | null) => {
			form.setSelectedErefId(nextErefId)

			const previousErefId = furniture.eref_id
			if (nextErefId === previousErefId) return
			if (!Number.isFinite(quotationId) || !quotationId) return

			// Versions for both the current and the new Eref are required for optimistic locking —
			// pull them from the cached relatedObjects.
			let previousEref: TEref | undefined
			let nextEref: TEref | undefined
			for (const page of relatedObjects) {
				if (!previousEref && previousErefId !== null) {
					previousEref = page.erefs.find((e) => e.id === previousErefId)
				}
				if (!nextEref && nextErefId !== null) {
					nextEref = page.erefs.find((e) => e.id === nextErefId)
				}
				if ((previousErefId === null || previousEref) && (nextErefId === null || nextEref)) break
			}
			if (nextErefId !== null && !nextEref) return

			await enqueue(async () => {
				try {
					let markPreviousUnconnected = false

					// 1) Disconnect from the current Eref (if any).
					if (previousErefId !== null && previousEref) {
						const disconnectResponse = await disconnectFurnitureFromEref({
							quotationId,
							erefId: previousErefId,
							body: {
								eref_version: previousEref.version,
								furniture: [{ id: furniture.id, version: versionRef.current }],
							},
						})
						const disconnectVersion = (disconnectResponse?.data as { version?: number } | undefined)?.version
						versionRef.current = typeof disconnectVersion === "number" ? disconnectVersion : versionRef.current + 1

						const nextPreviousErefVersion = previousEref.version + 1
						queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
							["quotation", quotationId],
							(cached) => bumpErefVersionInCache(cached, previousErefId, nextPreviousErefVersion),
						)

						markPreviousUnconnected = !relatedObjects.some((page) =>
							page.furniture.some((f) => f.id !== furniture.id && f.eref_id === previousErefId),
						)
					}

					// 2) Connect to the new Eref (if any).
					if (nextErefId !== null && nextEref) {
						const connectResponse = await connectFurnitureToEref({
							quotationId,
							erefId: nextErefId,
							body: {
								eref_version: nextEref.version,
								furniture: [{ id: furniture.id, version: versionRef.current }],
							},
						})
						const connectVersion = (connectResponse?.data as { version?: number } | undefined)?.version
						versionRef.current = typeof connectVersion === "number" ? connectVersion : versionRef.current + 1

						const nextNextErefVersion = nextEref.version + 1
						queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(
							["quotation", quotationId],
							(cached) => bumpErefVersionInCache(cached, nextErefId, nextNextErefVersion),
						)
					}

					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
						spliceFurnitureIntoCache(cached, {
							...furniture,
							version: versionRef.current,
							eref_id: nextErefId,
						}),
					)

					// Apply both eref status changes together (no await between them) so an ecall
					// linked to BOTH erefs settles on its final color in one synchronous pass,
					// instead of flashing through an intermediate color while the connect request
					// is still in flight.
					if (markPreviousUnconnected && previousErefId !== null) {
						AnnotationService.updateErefAnnotationStatus(previousErefId, "unconnected")
					}
					if (nextErefId !== null && nextEref) {
						AnnotationService.updateErefAnnotationStatus(nextErefId, "connected")
					}
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[
			form,
			furniture,
			relatedObjects,
			quotationId,
			enqueue,
			versionRef,
			connectFurnitureToEref,
			disconnectFurnitureFromEref,
			queryClient,
			formatError,
		],
	)

	const handleTopLevelCategoryChange = useCallback(
		(value: string | number | null) => {
			form.categorySelection.handleTopLevelChange(value)

			if (value !== OTHER_TOP_LEVEL_CATEGORY) return
			if (!Number.isFinite(quotationId) || !quotationId) return
			if (isOtherFurniture(furniture)) return

			// Backend clears the furniture's attachments config when the category becomes
			// "Other"; mirror that on the client so the form reflects the new defaults.
			// Locks inherit from the parent eref (false if unattached).
			const nextHasLocks = connectedEref?.estimation_config?.has_locks ?? false
			form.setAdjustableShelves(0)
			form.setHasLocks(nextHasLocks ? "yes" : "no")
			form.setFillings(0)
			form.setFinishingEnds(0)

			enqueue(async () => {
				try {
					const response = await updateFurnitureCategoryAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, related_category_id: null },
					})
					const data = response?.data as TUpdateFurnitureResponse | undefined
					const responseVersion = data?.this_furniture?.version
					const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
					versionRef.current = nextVersion

					// Prefer the authoritative furniture from the response; the hand-built
					// fallback only mirrors the backend's "Other" reset when this_furniture
					// is somehow absent (pre-#1767 behaviour). asOtherFurniture force-clears
					// every category level so the "Other" label shows even if this_furniture
					// omits those fields (spliceFurnitureIntoCache merges, so a stale category
					// would otherwise survive).
					const updatedFurniture: TFurniture = asOtherFurniture(
						data?.this_furniture ?? {
							...furniture,
							version: nextVersion,
							estimation_config: {
								furniture_id: furniture.id,
								lock_count: furniture.estimation_config?.lock_count ?? null,
								has_locks: nextHasLocks,
								number_of_fillers: 0,
								number_of_finishing_ends: 0,
								adjusted_shelves: 0,
							},
						},
					)
					AnnotationService.updateFurnitureAnnotation(updatedFurniture)
					const ordering = data?.ordering
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
						const next = spliceFurnitureIntoCache(cached, updatedFurniture)
						return ordering ? reorderFurnitureInCache(next, ordering) : next
					})
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[
			form,
			furniture,
			quotationId,
			enqueue,
			versionRef,
			queryClient,
			formatError,
			updateFurnitureCategoryAsync,
			connectedEref,
		],
	)

	const commitPrice = useCallback(() => {
		const next = form.price
		if (next === savedPriceRef.current) return
		if (next === null) return
		if (!Number.isFinite(quotationId) || !quotationId) return

		savedPriceRef.current = next
		enqueue(async () => {
			try {
				const response = await updateFurnitureFixedPriceAsync({
					quotationId,
					furnitureId: furniture.id,
					body: { version: versionRef.current, fixed_price: next },
				})
				const data = response?.data as
					| { version?: number; fixed_price_config?: TFurnitureFixedPriceConfig | null }
					| undefined
				const nextVersion = typeof data?.version === "number" ? data.version : versionRef.current + 1
				const nextPrice =
					typeof data?.fixed_price_config?.fixed_price === "number" ? data.fixed_price_config.fixed_price : next
				// Read the latest known name from savedNameRef (updated synchronously by
				// commitName before its mutation runs) so a queued price commit can't
				// overwrite a name that the user just typed and blurred.
				const nextName =
					data?.fixed_price_config?.name !== undefined
						? (data.fixed_price_config?.name ?? null)
						: savedNameRef.current === ""
							? null
							: savedNameRef.current
				versionRef.current = nextVersion
				savedPriceRef.current = nextPrice

				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
					spliceFurnitureIntoCache(cached, {
						...furniture,
						version: nextVersion,
						fixed_price_config: { fixed_price: nextPrice, name: nextName },
					}),
				)
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}, [
		form.price,
		quotationId,
		enqueue,
		versionRef,
		furniture,
		queryClient,
		formatError,
		updateFurnitureFixedPriceAsync,
	])

	const commitName = useCallback(() => {
		const trimmed = form.name.trim()
		if (trimmed === savedNameRef.current) return
		if (!Number.isFinite(quotationId) || !quotationId) return

		savedNameRef.current = trimmed
		const bodyName: string | null = trimmed === "" ? null : trimmed
		enqueue(async () => {
			try {
				const response = await updateFurnitureAsync({
					quotationId,
					furnitureId: furniture.id,
					body: { version: versionRef.current, name: bodyName },
				})
				const data = response?.data as TUpdateFurnitureResponse | undefined
				const updated = data?.this_furniture
				if (!updated) return
				if (typeof updated.version === "number") versionRef.current = updated.version
				savedNameRef.current = updated.fixed_price_config?.name ?? ""
				const ordering = data?.ordering
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
					const next = spliceFurnitureIntoCache(cached, updated)
					return ordering ? reorderFurnitureInCache(next, ordering) : next
				})
			} catch (e) {
				formatError(e)
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}, [form.name, quotationId, enqueue, versionRef, updateFurnitureAsync, furniture.id, queryClient, formatError])

	const handleObjectCategoryChange = useCallback(
		(value: string | number | null) => {
			form.categorySelection.handleModelCategoryChange(value)

			const nextId = typeof value === "number" ? value : value !== null ? Number(value) : null
			if (!Number.isFinite(nextId) || nextId === null || nextId === furniture.category?.id) return
			if (!Number.isFinite(quotationId) || !quotationId) return

			const nextCategoryName = form.categorySelection.modelCategoryOptions.find((opt) => opt.value === nextId)
				?.label as string | undefined
			const nextSuperCategoryName =
				typeof form.categorySelection.selectedSuperCategory === "string"
					? form.categorySelection.selectedSuperCategory
					: furniture.super_category?.name
			const nextTopLevelCategoryName =
				typeof form.categorySelection.selectedTopLevelCategory === "string"
					? form.categorySelection.selectedTopLevelCategory
					: furniture.top_level_category?.name

			// Pull model_label from the cached categories — the typed shape doesn't declare
			// `label`, but the API returns it on each category at runtime.
			type CategoryLite = { id: number; name?: string; label?: string; model_label?: string }
			type CategoriesByTopLevel = Record<string, Record<string, CategoryLite[]>>
			const supercategoryBucket = (objectsCategoriesData as CategoriesByTopLevel | undefined)?.[
				String(nextTopLevelCategoryName ?? "")
			]?.[String(nextSuperCategoryName ?? "")]
			const matchedCategory = supercategoryBucket?.find((c) => c.id === nextId)
			const nextModelLabel = matchedCategory?.label ?? matchedCategory?.model_label ?? ""

			enqueue(async () => {
				try {
					const response = await updateFurnitureCategoryAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, related_category_id: nextId },
					})
					// The category change cascades (model number disconnect, etc.), so the cache
					// is refetched below rather than spliced; the refetch returns the furniture
					// already in the backend-decided order. Read the new version from the
					// response when present, otherwise fall back to the +1 optimistic-locking bump.
					const responseVersion = (response?.data as TUpdateFurnitureResponse | undefined)?.this_furniture?.version
					const nextVersion = typeof responseVersion === "number" ? responseVersion : versionRef.current + 1
					versionRef.current = nextVersion

					const updatedFurniture: TFurniture = {
						...furniture,
						version: nextVersion,
						category: {
							id: nextId,
							name: nextCategoryName ?? furniture.category?.name ?? "",
							model_label: nextModelLabel,
						},
						super_category: {
							id: furniture.super_category?.id ?? 0,
							name: nextSuperCategoryName ?? furniture.super_category?.name ?? "",
						},
						top_level_category: {
							id: furniture.top_level_category?.id ?? 0,
							name: nextTopLevelCategoryName ?? furniture.top_level_category?.name ?? "",
						},
						// Backend disconnects the model number when category changes
						selected_model_number: null,
					}
					AnnotationService.updateFurnitureAnnotation(updatedFurniture)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[
			form.categorySelection,
			furniture,
			quotationId,
			enqueue,
			versionRef,
			queryClient,
			formatError,
			updateFurnitureCategoryAsync,
			objectsCategoriesData,
		],
	)

	const pageErefs = useMemo<TEref[]>(() => {
		const page = relatedObjects.find((p) => p.page_number === furniture.page_number)
		return page?.erefs ?? []
	}, [relatedObjects, furniture.page_number])

	const title = (form.selectedModelNumber?.model_number && furniture.category?.name) || "Object"
	const { data: catalogueImage } = useGetModelNumberImageLink(form.selectedModelNumber?.id ?? 0)
	const imageUrl = catalogueImage?.download_url ?? null

	const { data: selectedModelNumberDetails } = useGetModelNumber(form.selectedModelNumber?.id ?? 0)
	const { data: seriesList } = useGetSeries()
	const missingPriceSeriesName = useMemo<string | null>(() => {
		if (seriesOverrideId === null) return null
		const details = selectedModelNumberDetails as TModelNumberDetails | undefined
		if (!details) return null
		const hasPrice = details.pricing_info?.some((p) => p.series.id === seriesOverrideId) ?? false
		if (hasPrice) return null
		return seriesList?.find((s) => s.id === seriesOverrideId)?.series_name ?? null
	}, [seriesOverrideId, selectedModelNumberDetails, seriesList])

	const showAttachments = form.variant === "typical"
	const showModelNumber = form.variant === "typical"
	const showPrice = form.variant === "other"
	const showName = form.variant === "other"

	const updateStatus = useCallback(
		(next: TFurnitureStatus) => {
			if (!Number.isFinite(quotationId) || !quotationId) return
			enqueue(async () => {
				try {
					const response = await updateFurnitureStatusAsync({
						quotationId,
						furnitureId: furniture.id,
						body: { version: versionRef.current, status: next },
					})
					const updated = response?.data as TFurniture | undefined
					if (!updated) return
					versionRef.current = updated.version
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
						spliceFurnitureIntoCache(cached, updated),
					)
					AnnotationService.updateFurnitureEntityStatus(furniture.id, next)
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				}
			})
		},
		[quotationId, enqueue, versionRef, updateFurnitureStatusAsync, furniture.id, queryClient, formatError],
	)

	return (
		<div className={sidebar} data-hotkey-sidebar tabIndex={-1}>
			<FurnitureHeader
				title={title}
				status={furniture.status}
				onClose={onClose}
				onBack={furniture.eref_id != null && onNavigateToEref ? () => onNavigateToEref(furniture) : undefined}
			/>
			<div className={content}>
				<div className={segmentedWrapper}>
					<SegmentedWithoutBg
						options={TAB_OPTIONS}
						value={tab}
						onChange={(value) => setTab(value as TabOption)}
						size="middle"
						className={segmented}
					/>
				</div>
				{tab === "Connected callouts" ? (
					<ConnectedCalloutsTab
						ecalls={connectedEcalls}
						erefId={furniture.eref_id}
						onView={(e) => onView?.(e)}
					/>
				) : (
			<div className={body}>
				<FurnitureImage imageUrl={imageUrl} isCountertop={form.isCountertopItem} hasModelNumber={form.hasModelNumber} />

				<ElevationReferenceSelect erefs={pageErefs} value={form.selectedErefId} onChange={handleErefChange} />

				<div className={divider} />

				<CategoriesSection
					topLevelValue={form.categorySelection.selectedTopLevelCategory}
					topLevelOptions={form.categorySelection.topLevelOptions}
					onTopLevelChange={handleTopLevelCategoryChange}
					superValue={form.categorySelection.selectedSuperCategory}
					superOptions={form.categorySelection.superCategoryOptions}
					onSuperChange={form.categorySelection.handleSuperCategoryChange}
					modelCategoryValue={form.categorySelection.selectedModelCategory}
					modelCategoryOptions={form.categorySelection.modelCategoryOptions}
					onModelCategoryChange={handleObjectCategoryChange}
				/>

				<div className={divider} />

				<DimensionsSection
					width={form.width}
					height={form.height}
					depth={form.depth}
					onWidthChange={form.setWidth}
					onHeightChange={form.setHeight}
					onDepthChange={form.setDepth}
					onWidthCommit={() => commitDimension("width", form.width)}
					onHeightCommit={() => commitDimension("height", form.height)}
					onDepthCommit={() => commitDimension("depth", form.depth)}
					isRequired={form.variant !== "other"}
				/>

				{showModelNumber ? (
					<>
						<div className={divider} />
						<ModelNumberSection
							furniture={furniture}
							selectedModelNumber={form.selectedModelNumber}
							seriesOverrideId={seriesOverrideId}
							missingPriceSeriesName={missingPriceSeriesName}
							onSelect={commitSelectedModelNumber}
							isOther={form.variant === "other"}
							isRequired={form.variant !== "other"}
						/>
					</>
				) : null}

				{showName ? (
					<>
						<div className={divider} />
						<NameSection value={form.name} onChange={form.setName} onBlur={commitName} />
					</>
				) : null}

				{showPrice ? (
					<>
						{showName ? null : <div className={divider} />}
						<PriceSection value={form.price} onChange={form.setPrice} onBlur={commitPrice} />
					</>
				) : null}

				{showAttachments ? (
					<>
						<div className={divider} />
						<AttachmentsSection
							adjustableShelves={form.adjustableShelves}
							onAdjustableShelvesChange={form.setAdjustableShelves}
							onAdjustableShelvesCommit={() => {
								if (form.adjustableShelves === savedAdjustedShelvesRef.current) return
								savedAdjustedShelvesRef.current = form.adjustableShelves
								commitConfig({ adjusted_shelves: form.adjustableShelves })
							}}
							hasLocks={form.hasLocks}
							onHasLocksChange={(value) => {
								form.setHasLocks(value)
								commitConfig({ has_locks: value === "yes" })
							}}
							fillings={form.fillings}
							onFillingsChange={(value) => {
								form.setFillings(value)
								commitConfig({ number_of_fillers: value })
							}}
							finishingEnds={form.finishingEnds}
							onFinishingEndsChange={(value) => {
								form.setFinishingEnds(value)
								commitConfig({ number_of_finishing_ends: value })
							}}
						/>
					</>
				) : null}

				<div className={divider} />
				<NotesSection value={form.notes} onChange={form.setNotes} onBlur={commitNotes} />
			</div>
				)}
			</div>
			{tab === "Details" ? (
				<FurnitureFooter
					isReviewed={form.isReviewed}
					isInQuotation={form.isInQuotation}
					isPending={isStatusPending}
					onToggleReviewed={() => updateStatus(form.isReviewed ? "work_in_progress" : "reviewed")}
					canAddToQuotation={form.isValid && missingPriceSeriesName === null}
					onAddToQuotation={() => updateStatus("in_quotation")}
					onRemoveFromQuotation={() => updateStatus("reviewed")}
				/>
			) : null}
		</div>
	)
}
